import { IconArrowLeft } from 'central-icons/IconArrowLeft';
import { IconChevronRight } from 'central-icons/IconChevronRight';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import React, { isValidElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-use';

import { LoadingBox } from '@revelio/core';
import { Button, Text } from '@revelio/ui';

import { usePageLoadMonitor } from '../../hooks/usePageLoadMonitor';
import { useTutorialLinkHandler } from '../../hooks/useTutorialLinkHandler';
import { RlBox } from '../box/box';
import { TabsList, TabsPanel } from '../tabs';
import styles from './page.module.css';

const PAGE_CONTAINER_CLASS_NAME = 'revelio-page-container-class-name';

export type TitleType = string | string[];

export interface PageProps {
  title: TitleType;
  /** Rendered inline right after the title heading(s) (e.g. a toggle). */
  titleSuffix?: React.ReactNode;
  backPath?: string;
  topRight?: React.ReactNode;
  selections?: JSX.Element; //this will be the little chips that show what youve selected
  hideSelectionsMargins?: boolean;
  hideTop?: boolean;
  display?: 'flex' | 'block';
  children: React.ReactNode;
  loading?: boolean;
  scrollY?: boolean;
}

function headerTemplate(title: TitleType) {
  // biome-ignore lint/suspicious/noImplicitAnyLet: migrated from develop
  let arrayCopy;

  if (!Array.isArray(title)) {
    arrayCopy = [title];
  } else {
    arrayCopy = [...title];
  }

  if (arrayCopy.length === 0) {
    throw new ReferenceError(
      'Title must be a string or a non-empty array of strings'
    );
  }

  return (
    <div className={styles.pageHeaderLabel} data-testid="page-header-label">
      <Text
        as="h2"
        className={styles.pageTitle}
        fontSize="md"
        weight="semibold"
      >
        {arrayCopy.shift()}
      </Text>
      {arrayCopy.map((item, i) => {
        return (
          <div className={styles.breadcrumb} key={i}>
            <IconChevronRight size={16} className={styles.breadcrumbChevron} />
            <Text
              as="h2"
              className={styles.pageTitle}
              fontSize="md"
              weight="semibold"
            >
              {item}
            </Text>
          </div>
        );
      })}
    </div>
  );
}

export function Page({
  topRight,
  titleSuffix,
  backPath,
  selections,
  hideSelectionsMargins,
  hideTop,
  display = 'flex',
  loading,
  scrollY,
  ...props
}: PageProps) {
  const navigate = useNavigate();
  const originalChildren = React.Children.toArray(props.children);

  const children: {
    filtersContainer: React.ReactElement[];
    tabs: React.ReactElement[];
    other: ReactNode[];
  } = {
    filtersContainer: [],
    tabs: [],
    other: [],
  };
  const tabNames: string[] = [];
  let tabsEnabled = false;

  originalChildren.forEach((tItem) => {
    if (isValidElement(tItem)) {
      children.other.push(tItem);
    }
  });

  tabsEnabled = children.tabs.length > 0;

  const [tab, setTab] = useState<string>(tabNames[0] ?? '');

  const pageTitle = Array.isArray(props.title)
    ? props.title.join(' > ')
    : props.title;
  usePageLoadMonitor(pageTitle, loading);

  const location = useLocation();

  useTutorialLinkHandler(location);

  return (
    <div
      className={classNames(PAGE_CONTAINER_CLASS_NAME, styles.pageContainer)}
      data-display={display}
      data-scroll-y={hideTop || scrollY || undefined}
    >
      {!hideTop && (
        <div className={classNames(styles.pageHeader, styles.headerLayout)}>
          <div className={styles.titleGroup}>
            {backPath && (
              <Button
                aria-label="Go back"
                leftIcon={<IconArrowLeft size={16} />}
                size="xs"
                className={styles['backButton']}
                onClick={() => navigate(backPath)}
              />
            )}
            {headerTemplate(props.title)}
            {titleSuffix}
          </div>
          <div className={styles.spacer} />
          <div className={styles.headerActions}>{topRight}</div>
        </div>
      )}

      {selections && (
        <div
          className={styles.selections}
          data-hide-margins={hideSelectionsMargins || undefined}
        >
          {selections}
        </div>
      )}

      <div className={styles.toolbar} /*height="12"*/>
        {!!children.filtersContainer.length && (
          <div className={styles.filtersContainer}>
            {children.filtersContainer}
          </div>
        )}

        {tabNames.length > 0 && (
          <div className={styles.tabsContainer}>
            <TabsList tabs={tabNames} tab={tab} setTab={setTab} />
          </div>
        )}
      </div>

      <LoadingBox
        testId="page-data-loading"
        loading={loading}
        backgroundColorOpacity={1}
        height={{ base: 'unset', sm: 'fit-content' }}
        overflowY={{ base: 'scroll', md: 'unset' }}
      >
        {tabsEnabled ? (
          <TabsPanel tab={tab}>{children.tabs}</TabsPanel>
        ) : (
          <RlBox variant="large-container">{children.other}</RlBox>
        )}
      </LoadingBox>
    </div>
  );
}
